import type { AccountStatus, AdminCheckMethod, TimeZone } from '@libs/enums';
import type { ItemAttributes, OrEmpty, WithBlankSearchObject } from '@ps-smart-cp/ppfw-client';
import {
  Bool,
  Characters,
  Email,
  ItemName,
  Locale,
  SingleValue,
  ValueSet,
  WithBlankSearch,
} from '@ps-smart-cp/ppfw-client';

interface Schema {
  /**
   * ユーザID
   */
  readonly userId: string;
  /**
   * ユーザ名称
   */
  readonly userName: string;
  /**
   * Eメール
   */
  readonly email: string;
  /**
   * Eメール(抽出条件)
   */
  readonly emailCondition: WithBlankSearchObject;
  /**
   * 古いパスワード
   */
  readonly oldPassword: string;
  /**
   * 新しいパスワード
   */
  readonly newPassword: string;
  /**
   * 新しいパスワード(確認用)
   */
  readonly newPasswordCheck: string;
  /**
   * パスワード変更する
   */
  readonly passwordChange: boolean;
  /**
   * パスワード
   */
  readonly password: string;
  /**
   * パスワード(確認用)
   */
  readonly passwordCheck: string;
  /**
   * アカウント状態
   */
  readonly accountStatus: OrEmpty<AccountStatus>;
  /**
   * ロケール
   */
  readonly locale: OrEmpty<Locale>;
  /**
   * タイムゾーン
   */
  readonly timeZone: OrEmpty<TimeZone>;
  /**
   * システム管理者権限を付与するかどうか
   */
  readonly isSystemAdmin: boolean;
  /**
   * システム管理者権限(for Grid)
   */
  readonly systemAdmin: AdminCheckMethod;
  /**
   * システム管理者権限(抽出条件)
   */
  readonly systemAdminCondition: Set<AdminCheckMethod>;
  /**
   * 会社コード
   */
  readonly companyCode: string;
}

interface Record {
  readonly userId: string;
  readonly userName: string;
  readonly email: string;
  readonly emailCondition: string[];
  readonly isEmailBlankOnly: boolean;
  readonly accountStatus: string;
  readonly locale: string;
  readonly timeZone: string;
  readonly affiliatedCompany: number | null;
  readonly isSystemAdmin: boolean;
  readonly systemAdminCondition: string[];
  readonly password: string;
  readonly passwordCheck: string;
}

const attributes = {
  userId: Characters.preset.halfWidth({ maxLen: 8, required: true }),
  userName: Characters.preset.freeFormat(),
  oldPassword: Characters.preset.halfWidth({ minLen: 6, maxLen: 256, required: true }),
  newPassword: Characters.preset.halfWidth({ minLen: 6, maxLen: 256, required: true }),
  newPasswordCheck: Characters.preset.halfWidth({ minLen: 6, maxLen: 256, required: true }),
  passwordChange: Bool.new(),
  password: Characters.preset.halfWidth({ minLen: 6, maxLen: 256, required: true }),
  passwordCheck: Characters.preset.halfWidth({ minLen: 6, maxLen: 256, required: true }),
  email: Email.new(),
  emailCondition: WithBlankSearch.of(Characters.preset.freeFormat({ maxLen: 254 })),
  accountStatus: SingleValue.shortValue(true),
  locale: SingleValue.shortValue(true),
  timeZone: SingleValue.shortValue(true),
  isSystemAdmin: Bool.new(),
  systemAdminCondition: ValueSet.new(),
  companyCode: SingleValue.shortValue(true),
} satisfies ItemAttributes<Schema>;

const titleNames = {
  maintenance: ItemName.of(
    [Locale.ja, 'ログインユーザメンテナンス'],
    [Locale.en, 'Login User Maintenance Title'],
  ),
  registration: ItemName.of([Locale.ja, 'ログインユーザ登録'], [Locale.en, 'Logged in User Registration']),
  change: ItemName.of([Locale.ja, 'ログインユーザ変更'], [Locale.en, 'Login User Change']),
  changePassword: ItemName.of([Locale.ja, 'パスワード変更'], [Locale.en, 'Change Password']),
};

const names = {
  userId: ItemName.of([Locale.ja, 'ユーザID'], [Locale.en, 'User Id']),
  userName: ItemName.of([Locale.ja, 'ユーザ名称'], [Locale.en, 'User Name']),
  email: ItemName.of([Locale.ja, 'Eメール'], [Locale.en, 'Email']),
  oldPassword: ItemName.of([Locale.ja, '古いパスワード'], [Locale.en, 'Old Password']),
  newPassword: ItemName.of([Locale.ja, '新しいパスワード'], [Locale.en, 'New Password']),
  newPasswordCheck: ItemName.of(
    [Locale.ja, '新しいパスワード(確認用)'],
    [Locale.en, 'New Password (For Confirmation)'],
  ),
  passwordChange: ItemName.of([Locale.ja, 'パスワード変更する'], [Locale.en, 'Password Change']),
  password: ItemName.of([Locale.ja, 'パスワード'], [Locale.en, 'Password']),
  passwordCheck: ItemName.of([Locale.ja, 'パスワード(確認用)'], [Locale.en, 'Password Check']),
  accountStatus: ItemName.of([Locale.ja, 'アカウント状態'], [Locale.en, 'Account Status']),
  locale: ItemName.of([Locale.ja, 'ロケール'], [Locale.en, 'Locale']),
  timeZone: ItemName.of([Locale.ja, 'タイムゾーン'], [Locale.en, 'Timezone']),
  isSystemAdmin: ItemName.of(
    [Locale.ja, 'システム管理者の権限を付与する'],
    [Locale.en, 'Assign System Administrator Authority'],
  ),
  systemAdminCondition: ItemName.of([Locale.ja, 'システム管理者権限']),
  companyCode: ItemName.of([Locale.ja, '会社コード']),
  attachRole: ItemName.of([Locale.ja, 'ロール付与']),
};

const loginUserMst = {
  attributes,
  names,
  titleNames,
};

interface LoginUserMst {
  schema: Schema;
  record: Record;
}

export type { LoginUserMst };
export { loginUserMst };
